import { createIdempotencyKey } from '@/utils/idempotency.js';
import { resolveAuthenticatedHeaders } from '@/utils/requestContext.js';

const inFlight = new Map();
const message = 'No se pudo verificar la cobranza guardada. No vuelva a cobrar; contacte a soporte.';

function scopeKey(flow) {
  let headers;
  try { headers = resolveAuthenticatedHeaders(); }
  catch { throw new Error('No se pudo verificar la sesión y el almacenamiento de la cobranza.'); }
  return scopeFromHeaders(headers, flow);
}

function scopeFromHeaders(headers, flow) {
  const value = name => headers?.get?.(name) ?? headers?.[name];
  const tenant = value('X-Tenant');
  const user = value('X-User-Id');
  const branch = value('X-Sucursal-Id');
  if (!tenant || !user || !branch) throw new Error('No se pudo identificar la empresa, usuario y sucursal de la cobranza.');
  return `sinamon:receipt-operation:${JSON.stringify([tenant, String(user), String(branch), flow])}`;
}

function read(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const record = JSON.parse(raw);
    if (record.version !== 1 || record.scope !== key || !record.key ||
        !['pending', 'confirmed'].includes(record.status) || !record.payload ||
        JSON.stringify(record.payload) !== record.snapshot) throw new Error(message);
    return record;
  } catch { throw new Error(message); }
}

function write(key, record) {
  try {
    const raw = JSON.stringify(record);
    window.localStorage.setItem(key, raw);
    if (window.localStorage.getItem(key) !== raw) throw new Error(message);
  } catch { throw new Error(message); }
}

function locked(key, action) {
  if (!window.navigator.locks?.request) throw new Error('Este navegador no permite proteger la cobranza entre pestañas. Use un navegador compatible mediante HTTPS.');
  return window.navigator.locks.request(key, { mode: 'exclusive', ifAvailable: true }, (lock) => {
    if (!lock) throw new Error('La cobranza se está procesando en otra pestaña. Espere y consulte el resultado.');
    return action();
  });
}

export function readReceiptOperation(flow) {
  return read(scopeKey(flow));
}

// Pending operations have no expiry: a lost acknowledgement is not a failed payment.
export async function startNewReceiptOperation(flow) {
  const key = scopeKey(flow);
  if (inFlight.has(key)) throw new Error('La cobranza se está procesando.');
  return locked(key, () => {
    const record = read(key);
    if (record?.status === 'pending') throw new Error('Hay una cobranza pendiente de confirmación. Reintente la operación original.');
    window.localStorage.removeItem(key);
    if (window.localStorage.getItem(key) !== null) throw new Error(message);
  });
}

export async function executeReceiptOperation(flow, payload, send) {
  const key = scopeKey(flow);
  // Validate again at the transport boundary, after its asynchronous auth resolution.
  const assertScope = (headers) => {
    if (scopeKey(flow) !== key || (headers && scopeFromHeaders(headers, flow) !== key)) {
      throw new Error('La sesión cambió. Reabra la cobranza en la empresa original.');
    }
  };
  const snapshot = payload === undefined ? undefined : JSON.stringify(payload);
  if (inFlight.has(key)) {
    const active = inFlight.get(key);
    if (snapshot !== active.snapshot) throw new Error('La cobranza en curso tiene otros datos. Espere su confirmación.');
    return active.promise;
  }
  const operation = locked(key, async () => {
    let record = read(key);
    if (record?.status === 'confirmed') throw new Error('La cobranza ya fue confirmada. Seleccione Nueva cobranza para iniciar otra operación.');
    if (record && payload !== undefined) {
      const incoming = JSON.parse(snapshot);
      delete incoming.idempotencyKey;
      if (JSON.stringify(incoming) !== record.snapshot || (payload.idempotencyKey && payload.idempotencyKey !== record.key)) {
        throw new Error('Hay una cobranza pendiente con otros datos. Reintente los datos originales guardados.');
      }
    }
    if (!record) {
      const frozen = JSON.parse(snapshot);
      const suppliedKey = frozen.idempotencyKey;
      delete frozen.idempotencyKey;
      record = { version: 1, scope: key, key: suppliedKey || createIdempotencyKey('receipt'),
        status: 'pending', payload: frozen, snapshot: JSON.stringify(frozen) };
      write(key, record);
    }
    // Never send credentials from storage; callers resolve current auth separately.
    assertScope();
    const result = await send(JSON.parse(record.snapshot), record.key, assertScope);
    assertScope();
    write(key, { version: 1, scope: key, key: record.key, status: 'confirmed', payload: {}, snapshot: '{}' });
    return result;
  });
  inFlight.set(key, { promise: operation, snapshot });
  operation.finally(() => inFlight.delete(key)).catch(() => {});
  return operation;
}
